import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  Calendar,
  MapPin,
  DollarSign,
  Truck,
  Plus,
  Edit,
  Trash2,
  Download,
  AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Label } from './ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Progress } from './ui/progress';
import { Separator } from './ui/separator';
import { orderManager, formatCurrency, formatDate, formatQuantity, formatWeight } from '../mock';
import { useToast } from '../hooks/use-toast';

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dispatchDialogOpen, setDispatchDialogOpen] = useState(false);
  const [dispatchForm, setDispatchForm] = useState({
    dispatchDate: new Date().toISOString().split('T')[0],
    dispatchNumber: '',
    quantity: '',
    weightInKgs: ''
  });

  useEffect(() => {
    const orderData = orderManager.getOrderById(id);
    if (orderData) {
      setOrder(orderData);
    } else {
      toast({
        title: 'Order Not Found',
        description: 'The requested order could not be found.',
        variant: 'destructive'
      });
      navigate('/orders');
    }
    setLoading(false);
  }, [id, navigate, toast]);

  const handleAddDispatch = () => {
    console.log('handleAddDispatch called', dispatchForm);
    
    if (!dispatchForm.dispatchNumber.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Dispatch number is required.',
        variant: 'destructive'
      });
      return;
    }

    const quantity = parseFloat(dispatchForm.quantity);
    const weightInKgs = parseFloat(dispatchForm.weightInKgs);
    console.log('Parsed quantity:', quantity, 'weight:', weightInKgs);
    
    if (!quantity || quantity <= 0) {
      toast({
        title: 'Validation Error',
        description: 'Valid quantity is required.',
        variant: 'destructive'
      });
      return;
    }

    if (!weightInKgs || weightInKgs <= 0) {
      toast({
        title: 'Validation Error',
        description: 'Weight in Kgs must be greater than 0.',
        variant: 'destructive'
      });
      return;
    }

    if (quantity > order.balance) {
      toast({
        title: 'Validation Error',
        description: `Dispatch quantity cannot exceed balance (${formatQuantity(order.balance, order.unit)}).`,
        variant: 'destructive'
      });
      return;
    }

    const dispatchData = {
      ...dispatchForm,
      quantity,
      weightInKgs,
      unit: order.unit
    };
    
    console.log('Dispatch data:', dispatchData);
    console.log('Order ID:', order.id);

    const updatedOrder = orderManager.addDispatch(order.id, dispatchData);
    console.log('Updated order:', updatedOrder);
    
    if (updatedOrder) {
      setOrder(updatedOrder);
      setDispatchDialogOpen(false);
      setDispatchForm({
        dispatchDate: new Date().toISOString().split('T')[0],
        dispatchNumber: '',
        quantity: '',
        weightInKgs: ''
      });

      toast({
        title: 'Dispatch Added',
        description: `Dispatch ${dispatchForm.dispatchNumber} added successfully. Quantity: ${formatQuantity(quantity, order.unit)}, Weight: ${formatWeight(weightInKgs)}`,
      });
    } else {
      console.error('Failed to add dispatch');
      toast({
        title: 'Error',
        description: 'Failed to add dispatch. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const getStatusColor = (status) => {
    return status === 'Complete' 
      ? 'bg-green-100 text-green-800 border-green-200' 
      : 'bg-yellow-100 text-yellow-800 border-yellow-200';
  };

  const getProgressPercentage = () => {
    if (!order || order.quantity === 0) return 0;
    const dispatched = order.quantity - order.balance;
    return Math.round((dispatched / order.quantity) * 100);
  };

  const getTotalDispatchedWeight = () => {
    if (!order || !order.dispatches) return 0;
    return order.dispatches.reduce((total, dispatch) => {
      return total + (dispatch.weightInKgs || 0);
    }, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Order Not Found</h3>
          <p className="text-gray-600 mb-4">The requested order could not be found.</p>
          <Link to="/orders">
            <Button>Back to Orders</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link to="/orders">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{order.brandName}</h1>
              <p className="text-gray-600">Order Details - {order.poNumber}</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Link to={`/orders/${order.id}/edit`}>
              <Button variant="outline">
                <Edit className="w-4 h-4 mr-2" />
                Edit Order
              </Button>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Order Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Package className="w-5 h-5 mr-2" />
                    Order Information
                  </div>
                  <Badge className={getStatusColor(order.status)}>
                    {order.status}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Order Date</Label>
                      <div className="flex items-center mt-1">
                        <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                        <span className="text-gray-900">{formatDate(order.orderDate)}</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">PO Number</Label>
                      <p className="text-gray-900 mt-1">{order.poNumber}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Material Type</Label>
                      <p className="text-gray-900 mt-1">{order.materialType}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Total Quantity</Label>
                      <p className="text-gray-900 mt-1 font-semibold">
                        {formatQuantity(order.quantity, order.unit)}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Total Price</Label>
                      <div className="flex items-center mt-1">
                        <DollarSign className="w-4 h-4 mr-2 text-gray-400" />
                        <span className="text-gray-900 font-semibold">{formatCurrency(order.price)}</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Rate per {order.unit}</Label>
                      <p className="text-gray-900 mt-1">
                        {formatCurrency(order.price / order.quantity)}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Addresses */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  Addresses
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-medium text-gray-500">Bill To</Label>
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                      <p className="text-gray-900 whitespace-pre-line">{order.billTo}</p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">Ship To</Label>
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                      <p className="text-gray-900 whitespace-pre-line">{order.shipTo}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dispatch History */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Truck className="w-5 h-5 mr-2" />
                    Dispatch History
                  </div>
                  {order.balance > 0 && (
                    <Dialog open={dispatchDialogOpen} onOpenChange={setDispatchDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                          <Plus className="w-4 h-4 mr-2" />
                          Add Dispatch
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add New Dispatch</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="dispatchDate">Dispatch Date</Label>
                            <Input
                              id="dispatchDate"
                              type="date"
                              value={dispatchForm.dispatchDate}
                              onChange={(e) => setDispatchForm(prev => ({ ...prev, dispatchDate: e.target.value }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="dispatchNumber">Dispatch Number</Label>
                            <Input
                              id="dispatchNumber"
                              placeholder="e.g., DISP-007"
                              value={dispatchForm.dispatchNumber}
                              onChange={(e) => setDispatchForm(prev => ({ ...prev, dispatchNumber: e.target.value }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="dispatchQuantity">
                              Quantity ({order.unit}) - Max: {formatQuantity(order.balance, order.unit)}
                            </Label>
                            <Input
                              id="dispatchQuantity"
                              type="number"
                              step="0.01"
                              max={order.balance}
                              placeholder="Enter quantity"
                              value={dispatchForm.quantity}
                              onChange={(e) => setDispatchForm(prev => ({ ...prev, quantity: e.target.value }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="dispatchWeight">
                              Total Weight (Kgs)
                            </Label>
                            <Input
                              id="dispatchWeight"
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="Enter total weight in Kgs"
                              value={dispatchForm.weightInKgs}
                              onChange={(e) => setDispatchForm(prev => ({ ...prev, weightInKgs: e.target.value }))}
                            />
                          </div>
                          <div className="flex space-x-3 pt-4">
                            <Button onClick={handleAddDispatch} className="flex-1">
                              Add Dispatch
                            </Button>
                            <Button variant="outline" onClick={() => setDispatchDialogOpen(false)} className="flex-1">
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {order.dispatches.length === 0 ? (
                  <div className="text-center py-8">
                    <Truck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Dispatches Yet</h3>
                    <p className="text-gray-600 mb-4">This order hasn't been dispatched yet.</p>
                    <Dialog open={dispatchDialogOpen} onOpenChange={setDispatchDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="bg-blue-600 hover:bg-blue-700">
                          <Plus className="w-4 h-4 mr-2" />
                          Add First Dispatch
                        </Button>
                      </DialogTrigger>
                    </Dialog>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Dispatch Number</TableHead>
                          <TableHead className="text-right">Quantity</TableHead>
                          <TableHead className="text-right">Weight (Kgs)</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {order.dispatches.map((dispatch) => (
                          <TableRow key={dispatch.id}>
                            <TableCell>{formatDate(dispatch.dispatchDate)}</TableCell>
                            <TableCell className="font-medium">{dispatch.dispatchNumber}</TableCell>
                            <TableCell className="text-right">
                              {formatQuantity(dispatch.quantity, dispatch.unit)}
                            </TableCell>
                            <TableCell className="text-right">
                              {dispatch.weightInKgs ? formatWeight(dispatch.weightInKgs) : 'N/A'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Summary Sidebar */}
          <div className="space-y-6">
            {/* Progress Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Order Progress</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    {getProgressPercentage()}%
                  </div>
                  <Progress value={getProgressPercentage()} className="h-3 mb-4" />
                  <p className="text-sm text-gray-600">Order Completion</p>
                </div>
                
                <Separator />
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total Ordered</span>
                    <span className="font-medium">{formatQuantity(order.quantity, order.unit)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Dispatched</span>
                    <span className="font-medium text-green-600">
                      {formatQuantity(order.quantity - order.balance, order.unit)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Balance</span>
                    <span className="font-medium text-yellow-600">
                      {formatQuantity(order.balance, order.unit)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Dispatches</span>
                  <span className="font-medium">{order.dispatches.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Weight Dispatched</span>
                  <span className="font-medium text-blue-600">
                    {getTotalDispatchedWeight() > 0 ? formatWeight(getTotalDispatchedWeight()) : 'No weight recorded'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Order Value</span>
                  <span className="font-medium">{formatCurrency(order.price)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Pending Value</span>
                  <span className="font-medium text-yellow-600">
                    {formatCurrency((order.price * order.balance) / order.quantity)}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Alerts */}
            {order.balance > 0 && (
              <Card className="border-yellow-200 bg-yellow-50">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-yellow-800 mb-2">
                        Pending Dispatch
                      </h4>
                      <p className="text-sm text-yellow-700">
                        {formatQuantity(order.balance, order.unit)} remaining to be dispatched.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;